import React, { useMemo, useState } from 'react';
import { Button, DatePicker, Empty, Input, InputNumber, Select, Space, Switch, Tag, Tooltip, Typography } from 'antd';
import {
  ArrowLeftOutlined, CloseOutlined, ReloadOutlined, SwapOutlined, LockOutlined, StarOutlined, UndoOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { FctGlifoAtencion } from '../../../fct/react';
import { fct } from '../../../fct/tokens/tokens';
import { CampoEnfrentado, ComparacionDuplicados, ElementoMultivalor, TipoMultivalor } from '../../../api/clientesDuplicadosApi';
import {
  CambioElemento, EdicionFusion, TipoListaEditable, TipoPreferencia, cambiosDeReglas, celdaTocada, claveCampo,
  delDuplicadoEnLista, esNombreCompleto, nombreCompletoActual,
  filaEditable, formatearValor, listaFusionada, marcaFila, mismoValor, preferenciasActuales,
  preferenciasDelDuplicado, preferenciasDelSuperviviente, preferenciasTrasFlecha, puedeTraerPreferencias, puedeTraerDelDuplicado, valorActual, valorDelDuplicado,
} from './reglas';
import { Catalogos, Paso } from './useAsistenteDuplicados';

const { Text } = Typography;

const VERDE_FONDO = fct.estado.exito.relleno;
const ROJO_FONDO = fct.estado.riesgo.relleno;
const AMBAR = fct.estado.aviso.linea;
/** Franja de las etiquetas: mismo tono cálido que la cabecera de grupo, más claro. Marca el
 *  paralelismo de las dos columnas, que quedan del mismo ancho a su derecha. */
const FONDO_ETIQUETA = fct.superficie.papel;

const COLUMNAS = '170px 1fr 24px 1fr';

const CATALOGO_TIPO_TELEFONO = 'GC_C_TIPO_TELEFONO';
const CATALOGO_PREFERENCIA: Record<TipoPreferencia, string> = {
  PREF_PROVINCIA: 'GC_C_SUBREGION',
  PREF_TRAMO: 'GC_C_TRAMOS_FACT',
};

const vacio = (v: string | null | undefined) => v === null || v === undefined || v === '';

/** Lo que el operador puede hacer en el paso 4. Sin esto, la rejilla es de lectura. */
export interface AccionesEdicion {
  editarValor: (c: { tabla: string; campo: string }, valor: string | null) => void;
  restaurarValor: (c: { tabla: string; campo: string }) => void;
  cambiarElemento: (tipo: TipoListaEditable, idElemento: number, cambio: CambioElemento) => void;
  restaurarElemento: (tipo: TipoListaEditable, idElemento: number) => void;
  marcarPrincipal: (tipo: TipoListaEditable, idElemento: number | null) => void;
  fijarPreferencias: (tipo: TipoPreferencia, ids: number[] | undefined) => void;
  /** Pasa a la izquierda lo que las reglas de dato ganador toman del duplicado, en lo no tocado. */
  aplicarReglas: () => void;
}

/** Los dos mandos de la pareja, entre las cabeceras de las dos columnas. */
export interface ControlesPareja {
  puedeIntercambiar: boolean;
  intercambiada: boolean;
  /** Por qué no se puede intercambiar, para el tooltip. */
  motivoNoIntercambiar?: string;
  onIntercambiar: () => void;
  puedeRecargar: boolean;
  recargando: boolean;
  onRecargar: () => void;
}

interface FichasEnfrentadasProps {
  comparacion: ComparacionDuplicados | null;
  paso: Paso;
  /** El superviviente ya está fijado (paso 3 confirmado). Antes, la asignación es provisional (§4.1). */
  supervivienteFijado: boolean;
  edicion: EdicionFusion;
  catalogos: Catalogos;
  /** Solo se pasa en el paso 4. */
  acciones?: AccionesEdicion;
  /** El duplicado ya se ha eliminado (paso 6). */
  eliminado?: boolean;
  controles?: ControlesPareja;
}

/** Marca de la columna central en los pasos 2 y 3 (§4.2). */
const Marca: React.FC<{ k: CampoEnfrentado }> = ({ k }) => {
  const m = marcaFila(k);
  if (m === 'IGUAL') return <Tooltip title="Los dos valores coinciden"><Text type="secondary">=</Text></Tooltip>;
  if (m === 'DIFIERE') return <Tooltip title="Difieren y los dos tienen dato"><Text strong>≠</Text></Tooltip>;
  if (m === 'SOLO_UNO') return <Tooltip title="Solo uno tiene dato"><Text type="secondary">·</Text></Tooltip>;
  return null;
};

/** Señales que acompañan a la etiqueta del campo, en todos los pasos. Son atención (glifo del
 *  manual: urgente / atención / informativo), no estado: sin icono coloreado ni pastilla de color. */
const Senales: React.FC<{ k: CampoEnfrentado }> = ({ k }) => (
  <Space size={4}>
    {k.alarmaActiva && (
      <Tooltip title="Señal de alarma: este campo identifica a la persona y difiere. Puede que sean padre/hijo, no un duplicado.">
        <span><FctGlifoAtencion nivel="urgente" /></span>
      </Tooltip>
    )}
    {k.avisoClave && (
      <Tooltip title="Los dos tienen identificador y no coinciden: hay que mirarlo.">
        <span><FctGlifoAtencion nivel="atencion" /></span>
      </Tooltip>
    )}
    {k.claveCoincide && (
      <Tooltip title="Los dos comparten la misma clave externa: prueba fuerte de que son la misma ficha.">
        <Tag style={{ margin: 0, fontSize: 10, lineHeight: '16px' }}>misma clave</Tag>
      </Tooltip>
    )}
    {k.textoSucio && (
      <Tooltip title="Texto sucio: espacios sobrantes, doble espacio o caracteres invisibles. Se normaliza al fusionar.">
        <span><FctGlifoAtencion nivel="info" /></span>
      </Tooltip>
    )}
  </Space>
);

const Valor: React.FC<{ texto: string; tachado?: boolean }> = ({ texto, tachado }) => {
  if (!texto) return <Text type="secondary">—</Text>;
  return <Text delete={tachado} type={tachado ? 'secondary' : undefined} style={{ wordBreak: 'break-word' }}>{texto}</Text>;
};

const BotonRestaurar: React.FC<{ titulo?: string; onClick: () => void }> = ({ titulo = 'Restaurar', onClick }) => (
  <Tooltip title={titulo}>
    <Button size="small" type="text" icon={<UndoOutlined />} onClick={onClick} aria-label={titulo} />
  </Tooltip>
);

/**
 * Flecha ← de la columna central: trae a la izquierda (lo que queda) el dato de la derecha
 * (la ficha que se elimina), en vez de copiarlo y pegarlo a mano. Es el gesto del proceso 73 de
 * Auraquantic. Lo traído queda como elección del operador: se puede seguir editando encima.
 */
const BotonTraer: React.FC<{ titulo?: string; onClick: () => void }> = ({ titulo = 'Pasar este dato a la ficha que se conserva', onClick }) => (
  <Tooltip title={titulo}>
    <Button
      size="small"
      type="text"
      icon={<ArrowLeftOutlined />}
      onClick={onClick}
      aria-label={titulo}
      style={{ width: 24, minWidth: 24, padding: 0 }}
    />
  </Tooltip>
);

/** Control con el que se escribe en una celda, según el tipo de dato que declara el servidor. */
const EditorCelda: React.FC<{
  k: CampoEnfrentado;
  valor: string | null;
  catalogos: Catalogos;
  onChange: (v: string | null) => void;
}> = ({ k, valor, catalogos, onChange }) => {
  const ancho = { width: '100%' };
  switch (k.tipoDato) {
    case 'CATALOGO': {
      const opciones = (catalogos[k.catalogo ?? ''] ?? []).map((o) => ({ value: String(o.id), label: o.descripcion }));
      if (opciones.length === 0) return <Valor texto={formatearValor(valor)} />;
      return (
        <Select
          size="small"
          allowClear
          showSearch
          optionFilterProp="label"
          value={vacio(valor) ? undefined : valor!}
          options={opciones}
          onChange={(v) => onChange(v ?? null)}
          style={ancho}
        />
      );
    }
    case 'FECHA':
    case 'FECHA_HORA':
      return (
        <DatePicker
          size="small"
          format="DD/MM/YYYY"
          value={vacio(valor) ? null : dayjs(valor!.slice(0, 10))}
          onChange={(d) => onChange(d ? d.format('YYYY-MM-DD') : null)}
          style={ancho}
        />
      );
    case 'NUMERO':
      return (
        <InputNumber
          size="small"
          value={vacio(valor) || Number.isNaN(Number(valor)) ? null : Number(valor)}
          onChange={(v) => onChange(v === null || v === undefined ? null : String(v))}
          style={ancho}
        />
      );
    case 'BIT':
      return (
        <Select
          size="small"
          allowClear
          value={vacio(valor) ? undefined : valor!}
          options={[{ value: 'true', label: 'Sí' }, { value: 'false', label: 'No' }]}
          onChange={(v) => onChange(v ?? null)}
          style={ancho}
        />
      );
    case 'TEXTO_LARGO':
      return (
        <Input.TextArea
          size="small"
          autoSize={{ minRows: 1, maxRows: 6 }}
          value={valor ?? ''}
          onChange={(e) => onChange(e.target.value)}
        />
      );
    default:
      return (
        <Input
          size="small"
          maxLength={k.longitud ?? undefined}
          value={valor ?? ''}
          onChange={(e) => onChange(e.target.value)}
        />
      );
  }
};

/**
 * Las dos fichas enfrentadas, columna a columna, como en el proceso 73 de Auraquantic (§4.0):
 * izquierda = SE CONSERVA (verde), derecha = SE ELIMINA (rojo). En el paso 4 la izquierda es
 * lo que quedará (D-GC-42) y SE EDITA (script 030). Cada columna empieza con los datos de su
 * cliente (Dev, 2026-09-24); las flechas ← pasan datos de la derecha uno a uno, «aplicar reglas»
 * pasa de una vez lo que dicen las reglas de dato ganador, y todo lo cambiado en la izquierda
 * tiene su vuelta atrás al valor original.
 *
 * El set y su orden los manda el servidor (`FC_C_CLIENTE_FUSION_VISTA`, script 029): aquí se
 * recorre un solo listado de filas, que puede ser un campo, un campo de un bloque 1:1, un dato
 * de la ficha de compra o el hueco donde va una de las cinco listas.
 */
export const FichasEnfrentadas: React.FC<FichasEnfrentadasProps> = ({
  comparacion, paso, supervivienteFijado, edicion, catalogos, acciones, eliminado, controles,
}) => {
  /** Las filas vacías en las dos fichas se ven por defecto; el operador puede ocultarlas (Dev, 2026-09-24). */
  const [ocultarVacios, setOcultarVacios] = useState(false);

  const editando = paso === 4 && Boolean(acciones);
  const modoFusion = paso >= 4;

  /** Elementos de cada lista, por tipo y lado. */
  const listas = useMemo(() => {
    const m = new Map<string, ElementoMultivalor[]>();
    for (const e of comparacion?.multivalor ?? []) {
      const k = `${e.tipo}.${e.lado}`;
      if (!m.has(k)) m.set(k, []);
      m.get(k)!.push(e);
    }
    return m;
  }, [comparacion]);

  const elementos = (tipo: string, lado: 1 | 2) => listas.get(`${tipo}.${lado}`) ?? [];

  /** Una fila está vacía si no hay nada que enseñar en ninguno de los dos lados. */
  const filaVacia = (k: CampoEnfrentado) => (k.origen === 'LISTA'
    ? elementos(k.campo, 1).length === 0 && elementos(k.campo, 2).length === 0
    : k.comparacion === 'VACIOS');

  const grupos = useMemo(() => {
    if (!comparacion) return [];
    const orden: string[] = [];
    const porGrupo = new Map<string, CampoEnfrentado[]>();
    const visibles = comparacion.campos
      .filter((k) => k.mostrar)
      .filter((k) => !ocultarVacios || !filaVacia(k))
      .sort((a, b) => a.orden - b.orden);
    for (const k of visibles) {
      if (!porGrupo.has(k.grupo)) { porGrupo.set(k.grupo, []); orden.push(k.grupo); }
      porGrupo.get(k.grupo)!.push(k);
    }
    return orden.map((g) => ({ grupo: g, campos: porGrupo.get(g)! }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comparacion, ocultarVacios, listas]);

  const vacios = useMemo(
    () => (comparacion?.campos ?? []).filter((k) => k.mostrar && filaVacia(k)).length,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [comparacion, listas],
  );

  if (!comparacion) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description="Elige las dos fichas en el paso 1: aquí se enfrentarán columna a columna."
        style={{ padding: '48px 0' }}
      />
    );
  }

  const conserva = comparacion.fichas.find((f) => f.lado === 1)!;
  const elimina = comparacion.fichas.find((f) => f.lado === 2)!;
  const bloqueo = comparacion.regla.bloqueo;

  /** Texto de un valor de la celda izquierda: la descripción de catálogo si la hay. */
  const describir = (k: CampoEnfrentado, valor: string | null): string => {
    if (vacio(valor)) return '';
    if (mismoValor(valor, k.valorConserva, k.tipoDato) && k.descripcionConserva) return k.descripcionConserva;
    if (mismoValor(valor, k.valorElimina, k.tipoDato) && k.descripcionElimina) return k.descripcionElimina;
    if (k.catalogo) {
      const o = (catalogos[k.catalogo] ?? []).find((x) => String(x.id) === valor);
      if (o) return o.descripcion;
    }
    return formatearValor(valor);
  };

  /**
   * Cabecera de columna en una línea (Dev, 2026-09-24): «CONSERVAR · CL21345 Nombre Apellidos»
   * en verde, «ELIMINAR · …» en rojo. Lo que antes iba en dos líneas más (id, NIF, CU2,
   * farmacias) queda en el tooltip: está a mano sin cargar la cabecera.
   */
  const cabecera = (lado: 1 | 2) => {
    const f = lado === 1 ? conserva : elimina;
    const accion = bloqueo ? 'SIN DECIDIR' : lado === 1 ? 'CONSERVAR' : eliminado ? 'ELIMINADO' : 'ELIMINAR';
    // Familias del semáforo, no verde de marca (R-10): «se queda» es un resultado de éxito.
    const color = bloqueo ? AMBAR : lado === 1 ? fct.estado.exito.texto : fct.estado.riesgo.texto;
    const fondo = bloqueo ? fct.estado.aviso.relleno : lado === 1 ? VERDE_FONDO : ROJO_FONDO;
    const nombre = lado === 1 && modoFusion
      ? (nombreCompletoActual(comparacion.campos, edicion) ?? f.nombreCompleto)
      : f.nombreCompleto;
    const detalle = [
      `ID ${f.idCliente}`,
      f.nif && `NIF ${f.nif}`,
      f.idColegiadoCu2 !== null && `CU2 ${f.idColegiadoCu2}`,
      f.titularFarmacia && `titular de ${f.numeroFarmacias} farmacia${f.numeroFarmacias === 1 ? '' : 's'}`,
      f.idClienteWeb !== null && `web ${f.idClienteWeb}`,
    ].filter(Boolean).join(' · ');
    return (
      <Tooltip title={detalle} placement="bottom">
        <div style={{
          background: fondo, borderTop: `3px solid ${color}`, padding: '6px 10px', borderRadius: '4px 4px 0 0',
          display: 'flex', alignItems: 'center', gap: 6, minWidth: 0, height: '100%',
        }}
        >
          <Text strong style={{ color, whiteSpace: 'nowrap' }}>{accion}</Text>
          <Text strong style={{ whiteSpace: 'nowrap' }}>· {f.codigo ?? `ID ${f.idCliente}`}</Text>
          <Text ellipsis style={{ minWidth: 0, flex: 1 }}>{nombre}</Text>
        </div>
      </Tooltip>
    );
  };

  /** Etiqueta de la izquierda, común a campos y listas. */
  const etiqueta = (k: CampoEnfrentado, seEdita: boolean) => (
    <div style={{ padding: '6px 8px', background: FONDO_ETIQUETA, alignSelf: 'stretch' }}>
      <Space size={4} wrap>
        <Text style={{ fontSize: 12, fontWeight: 500, color: fct.tinta['900'] }}>{k.etiqueta}</Text>
        <Senales k={k} />
        {editando && !seEdita && k.origen !== 'LISTA' && (
          <Tooltip title="Este dato no se edita aquí: identificador, calculado o fijo.">
            <LockOutlined style={{ color: fct.tinta['500'], fontSize: 11 }} />
          </Tooltip>
        )}
      </Space>
    </div>
  );

  const fila = (k: CampoEnfrentado, izquierda: React.ReactNode, derecha: React.ReactNode, centro?: React.ReactNode, alinear: 'center' | 'start' = 'center') => (
    <div
      key={claveCampo(k)}
      style={{ display: 'grid', gridTemplateColumns: COLUMNAS, columnGap: 8, alignItems: alinear, borderBottom: `1px solid ${fct.linea.fila}` }}
    >
      {etiqueta(k, k.origen === 'LISTA' ? k.editable : filaEditable(k, comparacion.campos, comparacion.fichas))}
      <div style={{ padding: '4px 8px', minHeight: 34, display: 'flex', alignItems: 'center' }}>{izquierda}</div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', alignSelf: 'stretch', background: fct.superficie.hundida }}>
        {centro}
      </div>
      <div style={{ padding: '4px 8px', minHeight: 34, display: 'flex', alignItems: 'center', opacity: eliminado ? 0.5 : 1 }}>{derecha}</div>
    </div>
  );

  // ── Campos (CAMPO, BLOQUE e INFORMATIVO) ──
  const filaCampo = (k: CampoEnfrentado) => {
    const derecha = <Valor texto={formatearValor(k.valorElimina, k.descripcionElimina)} />;

    if (!modoFusion) {
      return fila(k, <Valor texto={formatearValor(k.valorConserva, k.descripcionConserva)} />, derecha, <Marca k={k} />);
    }

    // El nombre completo es calculado: se deriva de nombre y apellidos tal como están a la izquierda.
    const actual = esNombreCompleto(k)
      ? nombreCompletoActual(comparacion.campos, edicion) ?? valorActual(k, edicion)
      : valorActual(k, edicion);
    const tocada = celdaTocada(k, edicion);
    const seEdita = editando && filaEditable(k, comparacion.campos, comparacion.fichas);

    const izquierda = (
      <div style={{ width: '100%' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            {seEdita
              ? <EditorCelda k={k} valor={actual} catalogos={catalogos} onChange={(v) => acciones!.editarValor(k, v)} />
              : <Valor texto={describir(k, actual)} />}
          </div>
          {editando && tocada && <BotonRestaurar titulo="Recuperar el valor original" onClick={() => acciones!.restaurarValor(k)} />}
        </div>
      </div>
    );

    const centro = puedeTraerDelDuplicado(k, edicion, seEdita)
      ? <BotonTraer onClick={() => acciones!.editarValor(k, valorDelDuplicado(k))} />
      : undefined;

    return fila(k, izquierda, derecha, centro, 'start');
  };

  // ── Listas ──
  const elementoLectura = (m: ElementoMultivalor, lado: 1 | 2) => (
    <div key={m.idElemento} style={{ display: 'flex', gap: 6, alignItems: 'baseline', flexWrap: 'wrap' }}>
      {m.principal && <Tooltip title="Principal"><StarOutlined style={{ fontSize: 11 }} aria-label="Principal" /></Tooltip>}
      <Text style={{ wordBreak: 'break-word' }}>{m.valor}</Text>
      {m.tipo === 'FARMACIA' && <Text type="secondary" style={{ fontSize: 12 }}>#{m.idElemento}</Text>}
      {m.detalle && (
        m.tipo === 'TELEFONO'
          ? <Tag style={{ margin: 0, fontSize: 10, lineHeight: '16px' }}>{m.detalle}</Tag>
          : <Text type="secondary" style={{ fontSize: 12 }}>{m.detalle}</Text>
      )}
      {m.tipo === 'FARMACIA' && m.repetido && (
        <Tooltip title="Las dos fichas son titulares vigentes de esta misma farmacia: es la causa típica del bloqueo.">
          <Tag style={{ margin: 0, fontSize: 10, lineHeight: '16px' }}>la misma</Tag>
        </Tooltip>
      )}
      {lado === 2 && m.tipo !== 'FARMACIA' && m.repetido && !modoFusion && (
        <Tag style={{ margin: 0, fontSize: 10, lineHeight: '16px' }}>repetido</Tag>
      )}
    </div>
  );

  /** Email o teléfono de la lista fusionada, con sus controles en el paso 4. */
  const elementoFusionado = (tipo: TipoListaEditable, x: ReturnType<typeof listaFusionada>[number]) => {
    const m = x.elemento;
    const borrado = Boolean(x.cambio.eliminado);
    // Traer un teléfono con la flecha no cuenta como «tocado»: se deshace con la cruz.
    const tocado = borrado || x.cambio.valor !== undefined || x.cambio.idTipo !== undefined;
    const tiposTelefono = (catalogos[CATALOGO_TIPO_TELEFONO] ?? []).map((o) => ({ value: o.id, label: o.descripcion }));
    const descripcionTipo = tiposTelefono.find((t) => t.value === x.idTipo)?.label ?? m.detalle;

    const estrella = (
      <Tooltip title={x.principal ? 'Principal (pulsa para quitarlo)' : 'Marcar como principal'}>
        <Button
          size="small"
          type="text"
          disabled={!editando || borrado}
          // Iconos siempre de trazo (R-14): la activa se distingue por tinta y por aria-pressed.
          icon={<StarOutlined style={{ color: x.principal ? fct.marca['verde-oscuro'] : fct.linea.control }} />}
          onClick={() => acciones!.marcarPrincipal(tipo, x.principal ? null : m.idElemento)}
          aria-pressed={x.principal}
          aria-label={x.principal ? 'Principal' : 'Marcar como principal'}
        />
      </Tooltip>
    );

    let cuerpo: React.ReactNode;
    if (!editando || borrado) {
      cuerpo = (
        <Space size={6} wrap>
          <Valor texto={x.valor ?? ''} tachado={borrado} />
          {tipo === 'TELEFONO' && descripcionTipo && (
            <Tag style={{ margin: 0, fontSize: 10, lineHeight: '16px', textDecoration: borrado ? 'line-through' : undefined }}>{descripcionTipo}</Tag>
          )}
        </Space>
      );
    } else if (tipo === 'TELEFONO') {
      cuerpo = (
        <Space size={4} wrap>
          <Input
            size="small"
            style={{ width: 140 }}
            maxLength={40}
            value={x.valor ?? ''}
            onChange={(e) => acciones!.cambiarElemento(tipo, m.idElemento, { valor: e.target.value })}
          />
          <Select
            size="small"
            style={{ width: 110 }}
            placeholder="Tipo"
            value={x.idTipo ?? undefined}
            options={tiposTelefono}
            onChange={(v) => acciones!.cambiarElemento(tipo, m.idElemento, { idTipo: v })}
          />
        </Space>
      );
    } else {
      cuerpo = <Valor texto={x.valor ?? ''} />;
    }

    return (
      <div key={m.idElemento} style={{ display: 'flex', gap: 4, alignItems: 'center', minHeight: 28 }}>
        {(editando || x.principal) && estrella}
        <div style={{ flex: 1, minWidth: 0 }}>{cuerpo}</div>
        {editando && !borrado && (
          <Tooltip title="Eliminar">
            <Button
              size="small"
              type="text"
              icon={<CloseOutlined />}
              aria-label="Eliminar"
              onClick={() => (m.lado === 2
                // Lo traído del duplicado vuelve a la derecha, no se tacha.
                ? acciones!.cambiarElemento(tipo, m.idElemento, { anadido: false })
                : acciones!.cambiarElemento(tipo, m.idElemento, { eliminado: true }))}
            />
          </Tooltip>
        )}
        {editando && tocado && <BotonRestaurar onClick={() => acciones!.restaurarElemento(tipo, m.idElemento)} />}
      </div>
    );
  };

  const filaLista = (k: CampoEnfrentado) => {
    const tipo = k.campo as TipoMultivalor;
    const izq = elementos(tipo, 1);
    const der = elementos(tipo, 2);
    const derecha = der.length ? <div>{der.map((m) => elementoLectura(m, 2))}</div> : <Text type="secondary">—</Text>;

    if (!modoFusion || tipo === 'FARMACIA') {
      return fila(k, izq.length ? <div>{izq.map((m) => elementoLectura(m, 1))}</div> : <Text type="secondary">—</Text>, derecha, undefined, 'start');
    }

    if (tipo === 'EMAIL' || tipo === 'TELEFONO') {
      const lista = listaFusionada(tipo, comparacion.multivalor, edicion);
      /* La flecha va junto a cada elemento de la derecha (una fila de lista tiene varios; la
         columna central no sabría a cuál se refiere): lo añade a la izquierda. Emails y
         teléfonos igual: nada del duplicado entra solo. Los repetidos no llevan flecha, porque
         ya están a la izquierda con el mismo valor. */
      const flecha = (m: ElementoMultivalor) => {
        if (!editando || m.repetido || delDuplicadoEnLista(tipo, m, edicion)) return null;
        return (
          <BotonTraer
            titulo={tipo === 'EMAIL' ? 'Añadir este email a la ficha que se conserva' : 'Añadir este teléfono a la ficha que se conserva'}
            onClick={() => acciones!.cambiarElemento(tipo, m.idElemento, { anadido: true, eliminado: false })}
          />
        );
      };
      /* Las flechas van en la franja central, como las de los campos (Dev, 2026-09-24). Una fila
         de lista tiene varios elementos, así que la franja lleva una pila de huecos de la misma
         altura que los elementos de la derecha: cada flecha queda a la altura del suyo. */
      const ALTO_ELEMENTO = 28;
      const derechaLista = der.length ? (
        <div style={{ width: '100%', minWidth: 0 }}>
          {der.map((m) => (
            <div key={m.idElemento} style={{ height: ALTO_ELEMENTO, display: 'flex', alignItems: 'center', minWidth: 0, overflow: 'hidden' }}>
              {elementoLectura(m, 2)}
            </div>
          ))}
        </div>
      ) : <Text type="secondary">—</Text>;
      const centroLista = der.length ? (
        <div style={{ alignSelf: 'flex-start', paddingTop: 4, display: 'flex', flexDirection: 'column' }}>
          {der.map((m) => (
            <div key={m.idElemento} style={{ height: ALTO_ELEMENTO, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {flecha(m)}
            </div>
          ))}
        </div>
      ) : undefined;
      return fila(
        k,
        lista.length ? <div style={{ width: '100%' }}>{lista.map((x) => elementoFusionado(tipo, x))}</div> : <Text type="secondary">—</Text>,
        derechaLista, centroLista, 'start',
      );
    }

    // Preferencias de compra: selección múltiple sobre la ficha que quedará vigente.
    const pref = tipo as TipoPreferencia;
    const opciones = (catalogos[CATALOGO_PREFERENCIA[pref]] ?? []).map((o) => ({ value: o.id, label: o.descripcion }));
    const actuales = preferenciasActuales(pref, comparacion.campos, comparacion.multivalor, edicion);
    const tocada = edicion.preferencias[pref] !== undefined;
    const seEdita = editando && k.editable && comparacion.fichas.some((f) => f.fichaCompraVigente) && opciones.length > 0;
    const izquierda = seEdita ? (
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, width: '100%' }}>
        <Select
          mode="multiple"
          size="small"
          allowClear
          showSearch
          optionFilterProp="label"
          style={{ flex: 1, minWidth: 0 }}
          value={actuales}
          options={opciones}
          onChange={(ids) => acciones!.fijarPreferencias(pref, ids)}
        />
        {tocada && <BotonRestaurar titulo="Recuperar las preferencias originales" onClick={() => acciones!.fijarPreferencias(pref, undefined)} />}
      </div>
    ) : (
      actuales.length
        ? <div>{actuales.map((id) => <div key={id}><Text>{opciones.find((o) => o.value === id)?.label ?? id}</Text></div>)}</div>
        : <Text type="secondary">—</Text>
    );
    // Bloque: la flecha sustituye todas las preferencias de la izquierda por las del duplicado.
    const delDuplicado = preferenciasDelDuplicado(pref, comparacion.multivalor);
    const centro = puedeTraerPreferencias(actuales, delDuplicado, seEdita)
      ? (
        <BotonTraer
          titulo="Sustituir por las preferencias del duplicado"
          onClick={() => acciones!.fijarPreferencias(
            pref,
            preferenciasTrasFlecha(delDuplicado, preferenciasDelSuperviviente(pref, comparacion.multivalor)),
          )}
        />
      )
      : undefined;
    return fila(k, izquierda, derecha, centro, 'start');
  };

  /* «Aplicar reglas» como flecha, igual que las de cada fila pero mayor, en la calle central de la
     cabecera del primer grupo (Identidad): pasa de derecha a izquierda todo lo que dicen las
     reglas. Encima, el número de datos que cambiaría (Dev, 2026-09-24). */
  const flechaReglas = () => {
    const pendientes = cambiosDeReglas(comparacion, edicion);
    return (
      <Tooltip title="Reglas dato ganador">
        {/* La flecha queda centrada en la calle; el número, pegado a su derecha, sobre el hueco
            libre de la cabecera del grupo (la calle es más estrecha que los dos juntos). */}
        <div style={{ position: 'relative', display: 'flex', justifyContent: 'center', width: 24 }}>
          <Button
            type="text"
            icon={<ArrowLeftOutlined style={{ fontSize: 20 }} />}
            disabled={pendientes === 0}
            onClick={() => acciones!.aplicarReglas()}
            aria-label={`Reglas dato ganador: ${pendientes} datos`}
            style={{ width: 28, minWidth: 28, height: 28, padding: 0 }}
          />
          {pendientes > 0 && (
            <span style={{
              position: 'absolute', left: '100%', top: '50%', transform: 'translateY(-50%)', marginLeft: 4,
              fontSize: 12, fontWeight: 600, color: fct.marca['verde-oscuro'], whiteSpace: 'nowrap', pointerEvents: 'none',
            }}
            >
              {pendientes}
            </span>
          )}
        </div>
      </Tooltip>
    );
  };

  return (
    <div>
      {/* Encima de las cabeceras: nada se interpone entre ellas y la tabla (Dev, 2026-09-24). */}

      <div style={{ display: 'grid', gridTemplateColumns: COLUMNAS, columnGap: 8, marginBottom: 8, position: 'sticky', top: 0, zIndex: 2, background: fct.superficie.tarjeta, alignItems: 'stretch' }}>
        {/* Recargar, en el hueco de la franja de etiquetas; intercambiar, entre las dos cabeceras. */}
        <div style={{ background: FONDO_ETIQUETA, borderRadius: '4px 4px 0 0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {controles && (
            <Tooltip title="Recargar los dos clientes (sin perder lo hecho)">
              <Button
                size="small"
                type="text"
                icon={<ReloadOutlined />}
                loading={controles.recargando}
                disabled={!controles.puedeRecargar}
                onClick={controles.onRecargar}
                aria-label="Recargar los dos clientes"
              />
            </Tooltip>
          )}
        </div>
        {cabecera(1)}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {controles && (
            <Tooltip title={controles.puedeIntercambiar
              ? (controles.intercambiada ? 'Columnas intercambiadas: pulsa para volver a la sugerencia' : 'Intercambiar columnas')
              : (controles.motivoNoIntercambiar ?? 'No se puede intercambiar')}
            >
              <Button
                size="small"
                type={controles.intercambiada ? 'default' : 'text'}
                icon={<SwapOutlined style={{ fontSize: 16 }} />}
                disabled={!controles.puedeIntercambiar}
                onClick={controles.onIntercambiar}
                aria-label="Intercambiar columnas"
                style={{ width: '100%', height: '100%', minWidth: 0, padding: 0 }}
              />
            </Tooltip>
          )}
        </div>
        {cabecera(2)}
      </div>

      {grupos.map((g, i) => {
        /* Un grupo de una sola fila que se llama igual que ella (Farmacia, Teléfonos) no necesita
           subtítulo: repetiría la etiqueta (Dev, 2026-09-24). Se mantiene el aire entre grupos. */
        const redundante = g.campos.length === 1
          && g.campos[0].etiqueta.trim().toLowerCase() === g.grupo.trim().toLowerCase();
        return (
          <div key={g.grupo} style={redundante ? { marginTop: 8 } : undefined}>
            {!redundante && (i === 0 && editando ? (
              <div style={{
                display: 'grid', gridTemplateColumns: COLUMNAS, columnGap: 8, alignItems: 'center',
                background: fct.superficie.hundida, marginTop: 8, borderRadius: 4,
              }}
              >
                <Text strong style={{ fontSize: 12, padding: '4px 8px' }}>{g.grupo}</Text>
                <div />
                <div style={{ display: 'flex', justifyContent: 'center' }}>{flechaReglas()}</div>
                <div />
              </div>
            ) : (
              <div style={{ background: fct.superficie.hundida, padding: '4px 8px', marginTop: 8, borderRadius: 4 }}>
                <Text strong style={{ fontSize: 12 }}>{g.grupo}</Text>
              </div>
            ))}
            {g.campos.map((k) => (k.origen === 'LISTA' ? filaLista(k) : filaCampo(k)))}
          </div>
        );
      })}

      {/* Al final del recuadro, para no separar las cabeceras de la tabla. */}
      {vacios > 0 && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
          <Space size={8}>
            <Text type="secondary" style={{ fontSize: 12 }}>Ocultar {vacios} filas vacías en las dos fichas</Text>
            <Switch size="small" checked={ocultarVacios} onChange={setOcultarVacios} />
          </Space>
        </div>
      )}

      {grupos.length === 0 && (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Las dos fichas están vacías en todos los campos del set." />
      )}
    </div>
  );
};
